import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, } from "@mui/icons-material";
import { googleReviewsUrl } from "../lib/propertyProvenance";
import { PropertyMoney, PropertyContacts, PropertyActivity, LearnedCommission } from "./properties/PropertyHistoryPanels";
// components/PropertiesPage.tsx
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  usePropertiesEnriched,
  usePropertyStats,
  useNeighborhoods,
  useManagementCompanies,
  usePropertyInvalidation,
  propertyKeys,
  PROPERTY_STALE_TIME,
  PROPERTY_GC_TIME,
} from "../hooks/usePropertyData";
import PageRefreshButton from "./PageRefreshButton";
import PageLoadingScaffold from "./common/PageLoadingScaffold";
import { EmptyState } from "./common";
import { PropertiesSectionHeader, propertyUpdateAge } from "./PropertiesShell";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Drawer,
  Divider,
  Badge,
  Tooltip,
  alpha,
  Skeleton,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Collapse,
  Switch,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Avatar,
  LinearProgress,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Popover,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  LocationOn as LocationIcon,
  Apartment as ApartmentIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Language as WebsiteIcon,
  Pets as PetsIcon,
  LocalOffer as SpecialIcon,
  Percent as CommissionIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ContentCopy as CopyIcon,
  Layers as LayersIcon,
  Home as HomeIcon,
  CheckCircle as AvailableIcon,
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { fetchWithAuth } from "../lib/api";
import { buildCapabilities } from "../lib/capabilities";
import { usePersistedState } from "../hooks/usePersistedState";
import { isPropertiesFilterState } from "../lib/persistenceRegistry";
import AvailabilityTimeline from "./MarketInsights/components/AvailabilityTimeline";
import UnitsListDrawer from "./MarketInsights/components/UnitsListDrawer";
import UnitDetailDrawer from "./MarketInsights/components/UnitDetailDrawer";
import PropertyEnrichmentPanel from "./properties/PropertyEnrichmentPanel";
import { getSignalStripSignals } from "../lib/propertyAttributes";
import {
  useAvailabilityTimeline,
  useUnitsByDate,
  useUnitDetail,
} from "./MarketInsights/hooks/useMarketInsights";
import type { UnitDetail } from "./MarketInsights/types";
import { parseAppNavigationState, replaceNavigationState, resolvePropertyNavigationState, resolveSelectedPropertyForWrite } from "../lib/appNavigationState";
import { parseCalendarDate } from "../lib/calendarDate";

// Types
interface Property {
  current_special?: {text:string;ends_on:string;captured_at?:string|null} | null;
  learned_commission?: LearnedCommission | null;
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  neighborhood: string | null;
  url: string | null;
  property_link: string | null;
  property_website: string | null;
  property_management: string | null;
  s3_image_url: string | null;
  phone: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  website: string | null;
  pet_policy: string | null;
  pet_policy_details?: string | null;
  amenities: string[] | null;
  latitude: number | null;
  longitude: number | null;
  total_units: number | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  specials: string | null;
  specials_summary: string | null;
  agent_commission: number | null;
  commission_type: string | null;
  commission_with_tour: number | null;
  commission_without_tour: number | null;
  pet_fee: number | null;
  pet_deposit?: number | null;
  admin_fee: number | null;
  application_fee: number | null;
  notes: string | null;
  is_favorite: boolean;
  last_scraped?: string | null;
  total_floor_plans?: number | null;
  min_rent?: number | null;
  max_rent?: number | null;
  // Enhanced fields from unit data
  available_units?: number;
  available_bed_baths?: string[];
  floor_plan_count?: number;
  floor_plans_missing_images?: number;
  min_price?: number;
  max_price?: number;
  // PKT-0246 · enrichment already on the /enriched wire but previously unrendered.
  attributes?: Record<string, unknown> | null;
  description?: string | null;
  rating?: number | null;
  review_count?: number | null;
  office_hours?: string | null;
  attributes_source?: string | null;
  attributes_updated_at?: string | null;
}

interface PropertyStats {
  total: number;
  with_specials: number;
  with_commission: number;
  favorites: number;
  by_neighborhood: Record<string, number>;
  by_management: Record<string, number>;
}

interface FloorPlan {
  property_id: string;
  id: string;
  model_name: string | null;
  bedrooms: number | null;
  bathrooms: number | null;
  square_feet: number | null;
  available_units: number | null;
  min_price: number | null;
  max_price: number | null;
  image_url: string | null;
  s3_image_url: string | null;
}

interface FloorPlansResponse {
  property_id: string;
  property_name: string;
  total_floor_plans: number;
  floor_plans: FloorPlan[];
}

const PROPERTY_DIALOG_TAB = {
  overview: 0,
  floorPlans: 1,
  commissionNotes: 2,
  specialsFees: 3,
  details: 4,
} as const;

const EDITABLE_PROPERTY_FIELDS: Array<keyof Property> = [
  "agent_commission",
  "commission_type",
  "commission_with_tour",
  "commission_without_tour",
  "pet_deposit",
  "admin_fee",
  "application_fee",
  "specials",
  "specials_summary",
  "pet_policy_details",
  "notes",
  "phone",
  "property_website",
  "address",
  "city",
  "state",
  "zip_code",
  "neighborhood",
  "total_units",
  "active",
];

const buildPropertyUpdatePayload = (
  original: Property,
  updates: Partial<Property>
): Partial<Property> => {
  return EDITABLE_PROPERTY_FIELDS.reduce((payload, field) => {
    const previousValue = original[field];
    const nextValue = updates[field];

    if (previousValue !== nextValue) {
      payload[field] = nextValue as never;
    }

    return payload;
  }, {} as Partial<Property>);
};

interface FilterState {
  search: string;
  neighborhoods: string[];
  propertyManagement: string[];
  hasPetPolicy: boolean | null;
  hasSpecials: boolean | null;
  hasCommission: boolean | null;
  showFavoritesOnly: boolean;
  showActiveOnly: boolean;
  noFloorPlanImage: boolean;
}
const isFilterState = (value: unknown): value is FilterState => {
  return isPropertiesFilterState(value);
};

// =============================================================================
// API Functions - WITH AUTH
// =============================================================================

// Data fetching moved to hooks/usePropertyData.ts (React Query cached)

const updateProperty = async (
  id: string,
  updates: Partial<Property>
): Promise<Property> => {
  const {
    id: _,
    created_at,
    updated_at,
    available_units,
    floor_plan_count,
    min_price,
    max_price,
    ...updateData
  } = updates as any;
  return fetchWithAuth<Property>(`/properties-management/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updateData),
  });
};

const getDisplaySpecials = (specials: string | null) => {
  const normalized = specials?.trim();
  if (!normalized) return null;
  if (/^(n\/a|none)$/i.test(normalized)) return null;
  return normalized;
};

const getDisplayWebsite = (property: Property) =>
  property.property_website || property.property_link || property.website || null;

const getDisplayPhone = (property: Property) =>
  property.phone || property.contact_phone || null;

const getDisplayPetPolicy = (property: Property) =>
  property.pet_policy_details || property.pet_policy || null;

const getDisplayPetDeposit = (property: Property) =>
  property.pet_deposit ?? property.pet_fee ?? null;

const toggleFavorite = async (id: string): Promise<Property | null> => {
  try {
    return await fetchWithAuth<Property>(
      `/properties-management/${id}/favorite`,
      { method: "PATCH" }
    );
  } catch {
    return null;
  }
};

// =============================================================================
// Property Card Component
// =============================================================================

interface PropertyCardProps {
  property: Property;
  onOpen: (property: Property) => void;
  onToggleFavorite: (id: string) => void;
  viewMode: "grid" | "list";
  isDemo?: boolean;
}

const PropertyCard: React.FC<PropertyCardProps> = ({
  property,
  onOpen,
  onToggleFavorite,
  viewMode,
  isDemo = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expanded, setExpanded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [phoneAnchor, setPhoneAnchor] = useState<HTMLElement | null>(null);
  const [phoneCopied, setPhoneCopied] = useState(false);
  const displaySpecials = getDisplaySpecials(property.current_special?.text || null);
  const rawSummary = getDisplaySpecials(property.current_special?.text || null);
  const displaySpecialsSummary = rawSummary && !rawSummary.endsWith("...") ? rawSummary : (displaySpecials ? "Leasing Special" : null);
  const displayWebsite = getDisplayWebsite(property);
  const displayPetPolicy = getDisplayPetPolicy(property);
  const displayPetDeposit = getDisplayPetDeposit(property);
  const displayFloorPlanCount =
    property.floor_plan_count ?? property.total_floor_plans ?? null;

  const displayPhone = property.phone || property.contact_phone || null;
  const formatPhone = (phone: string) => {
    const digits = phone.replace(/\D/g, "");
    if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
    return phone;
  };

  const fullAddress = [
    property.address,
    property.city,
    property.state,
    property.zip_code,
  ]
    .filter(Boolean)
    .join(", ");

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(fullAddress);
  };

  const handleCardOpen = () => {
    onOpen(property);
  };

  const handleCardKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleCardOpen();
    }
  };

  const stopCardClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const priceRangeLabel = () => {
    const minRent = property.min_price ?? property.min_rent ?? null;
    const maxRent = property.max_price ?? property.max_rent ?? null;

    if (minRent !== null && maxRent !== null) {
      if (minRent === maxRent) {
        return `$${minRent.toLocaleString()}`;
      }
      return `$${minRent.toLocaleString()} - $${maxRent.toLocaleString()}`;
    }

    if (minRent !== null) {
      return `$${minRent.toLocaleString()}`;
    }

    if (maxRent !== null) {
      return `$${maxRent.toLocaleString()}`;
    }

    return null;
  };

  if (viewMode === "list") {
    if (isMobile) {
      return (
        <Box
          onClick={handleCardOpen}
          onKeyDown={handleCardKeyDown}
          role="button"
          tabIndex={0}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 1.5,
            py: 1.25,
            cursor: "pointer",
            borderBottom: "1px solid",
            borderColor: "divider",
            "&:active": {
              bgcolor: alpha(theme.palette.primary.main, 0.06),
            },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Row 1: name + price */}
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{ flex: 1, minWidth: 0 }}
              >
                {property.name}
              </Typography>
              {Number(property.learned_commission?.value)>0 && <Chip size="small" label="Pays commission" />}
              {priceRangeLabel() && (
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="primary.main"
                  sx={{
                    whiteSpace: "nowrap",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {priceRangeLabel()}
                </Typography>
              )}
            </Box>

            {/* Row 2: neighborhood | available · plans */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 0.25,
                color: "text.secondary",
                minWidth: 0,
              }}
            >
              <Typography
                variant="caption"
                noWrap
                sx={{ flex: 1, minWidth: 0 }}
              >
                {property.neighborhood || fullAddress || "—"}
              </Typography>
              {!isDemo && property.last_scraped && (
                <Typography
                  variant="caption"
                  noWrap
                  sx={{ flexShrink: 0, color: "text.disabled" }}
                >
                  Updated {formatFreshnessDate(property.last_scraped)}
                </Typography>
              )}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  flexShrink: 0,
                }}
              >
                {property.available_units !== undefined && (
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    sx={{
                      color: "success.main",
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {property.available_units} avail
                  </Typography>
                )}
                {property.available_units !== undefined &&
                  displayFloorPlanCount !== null && (
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.45, fontSize: "0.65rem" }}
                    >
                      ·
                    </Typography>
                  )}
                {displayFloorPlanCount !== null && (
                  <Typography
                    variant="caption"
                    sx={{
                      whiteSpace: "nowrap",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {displayFloorPlanCount} plans
                  </Typography>
                )}
              </Box>
            </Box>

            {/* Row 3 (optional): inline leasing special */}
            {displaySpecials && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  mt: 0.5,
                  minWidth: 0,
                }}
              >
                <SpecialIcon
                  sx={{ fontSize: 14, color: "warning.main", flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  color="warning.main"
                  fontWeight={600}
                  noWrap
                  sx={{ minWidth: 0 }}
                >
                  {displaySpecialsSummary}
                </Typography>
              </Box>
            )}
          </Box>

          <IconButton
            size="small"
            onClick={(event) => {
              stopCardClick(event);
              onToggleFavorite(property.id);
            }}
            sx={{ ml: 0.25, flexShrink: 0 }}
          >
            {property.is_favorite ? (
              <StarIcon fontSize="small" sx={{ color: "warning.main" }} />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      );
    }

    return (
      <Card
        onClick={handleCardOpen}
        onKeyDown={handleCardKeyDown}
        role="button"
        tabIndex={0}
        sx={{
          display: "flex",
          mb: 2,
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: theme.shadows[8],
          },
        }}
      >
        <CardMedia
          component="div"
          sx={{
            width: 220,
            minHeight: 160,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
          }}
        >
          {property.s3_image_url && !imageError ? (
            <Box
              component="img"
              src={property.s3_image_url}
              alt={property.name}
              onError={() => setImageError(true)}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ApartmentIcon
              sx={{ fontSize: 48, color: "text.secondary", opacity: 0.5 }}
            />
          )}
          {displaySpecials && (
            <Tooltip title={displaySpecials}>
              <Chip
                label={displaySpecialsSummary}
                size="small"
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  fontWeight: 600,
                  bgcolor: "#fff",
                  color: "#111",
                  boxShadow: 2,
                  maxWidth: 180,
                  "& .MuiChip-label": {
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
              />
            </Tooltip>
          )}
        </CardMedia>

        <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
          <CardContent sx={{ flex: "1 0 auto", pb: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  {property.name}
                </Typography>
                {Number(property.learned_commission?.value)>0 && <Chip size="small" label="Pays commission" />}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                >
                  <LocationIcon fontSize="small" />
                  {property.neighborhood ||
                    fullAddress ||
                    "Address not available"}
                </Typography>
                {!isDemo && property.last_scraped && (
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ display: "block", mt: 0.25 }}
                  >
                    Updated {formatFreshnessDate(property.last_scraped)}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <IconButton
                  size="small"
                  onClick={(event) => {
                    stopCardClick(event);
                    onToggleFavorite(property.id);
                  }}
                >
                  {property.is_favorite ? (
                    <StarIcon sx={{ color: "warning.main" }} />
                  ) : (
                    <StarBorderIcon />
                  )}
                </IconButton>
              </Box>
            </Box>

            {/* Stats Row */}
            <Box sx={{ display: "flex", gap: 3, mt: 2, mb: 1.5 }}>
              {property.available_units !== undefined && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AvailableIcon fontSize="small" color="success" />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="success.main"
                  >
                    {property.available_units} available
                  </Typography>
                </Box>
              )}
              {displayFloorPlanCount !== null && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LayersIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {displayFloorPlanCount} floor plans
                  </Typography>
                </Box>
              )}
              {priceRangeLabel() && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <TrendingUpIcon fontSize="small" color="primary" />
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="primary.main"
                  >
                    {priceRangeLabel()}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* PKT-0246 · enrichment signal strip (fixed priority order so cards align) */}
            {(() => {
              const signals = getSignalStripSignals(property.attributes, 5);
              if (signals.length === 0) return null;
              return (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.75 }}>
                  {signals.map((s) => (
                    <Chip
                      key={s.key}
                      size="small"
                      label={s.label}
                      variant="outlined"
                      sx={{
                        height: 22,
                        fontSize: "0.68rem",
                        fontWeight: 600,
                        bgcolor: "action.hover",
                        borderColor: "divider",
                      }}
                    />
                  ))}
                </Box>
              );
            })()}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mt: 0.5,
              }}
            >
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, flex: 1 }}>
                {property.property_management && (
                  <Chip
                    icon={<BusinessIcon />}
                    label={property.property_management}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {(property.commission_type && property.commission_with_tour) && (
                  <Tooltip
                    placement="right"
                    title={
                      <Box sx={{ textAlign: "left" }}>
                        <Typography variant="caption" fontWeight={700} display="block" color="#fff">Commission</Typography>
                        <Typography variant="caption" display="block">
                          With Tour: {property.commission_type === "flat" ? `$${property.commission_with_tour}` : `${property.commission_with_tour}% of base rent`}
                        </Typography>
                        {property.commission_without_tour && (
                          <Typography variant="caption" display="block">
                            Without Tour: {property.commission_type === "flat" ? `$${property.commission_without_tour}` : `${property.commission_without_tour}% of base rent`}
                          </Typography>
                        )}
                      </Box>
                    }
                  >
                    <Chip
                      icon={<CommissionIcon />}
                      label={property.commission_type === "flat"
                        ? `$${property.commission_with_tour}`
                        : `${property.commission_with_tour}% Base Rent`
                      }
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {displayWebsite && (
                  <Tooltip title="Visit website">
                    <IconButton
                      size="small"
                      href={displayWebsite}
                      target="_blank"
                      onClick={stopCardClick}
                    >
                      <WebsiteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Copy address">
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      stopCardClick(event);
                      handleCopyAddress();
                    }}
                  >
                    <CopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </CardContent>
        </Box>
      </Card>
    );
  }

  // Grid view
  return (
    <Card
      onClick={handleCardOpen}
      onKeyDown={handleCardKeyDown}
      role="button"
      tabIndex={0}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "all 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: theme.shadows[12],
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        <CardMedia
          sx={{
            height: 160,
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {property.s3_image_url && !imageError ? (
            <Box
              component="img"
              src={property.s3_image_url}
              alt={property.name}
              onError={() => setImageError(true)}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ApartmentIcon
              sx={{ fontSize: 64, color: "text.secondary", opacity: 0.3 }}
            />
          )}
        </CardMedia>

        {/* Badges overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            right: 8,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", gap: 0.5 }}>
            {displaySpecials && (
              <Tooltip title={displaySpecials}>
                <Chip
                  label={displaySpecialsSummary}
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: "#fff",
                    color: "#111",
                    boxShadow: 2,
                    maxWidth: 200,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={(event) => {
              stopCardClick(event);
              onToggleFavorite(property.id);
            }}
            sx={{
              bgcolor: alpha(theme.palette.background.paper, 0.9),
              "&:hover": { bgcolor: theme.palette.background.paper },
            }}
          >
            {property.is_favorite ? (
              <StarIcon sx={{ color: "warning.main" }} />
            ) : (
              <StarBorderIcon />
            )}
          </IconButton>
        </Box>

        {/* Available units badge */}
        {property.available_units !== undefined &&
          property.available_units > 0 && (
            <Tooltip placement="top" title={property.available_bed_baths?.length ? (
              <>{property.available_bed_baths.map((bb, i) => <span key={i}>{bb}{i < property.available_bed_baths!.length - 1 && <br/>}</span>)}</>
            ) : ""}>
              <Chip
                label={`${property.available_units} units available`}
                size="small"
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.common.black, 0.45),
                  color: "#fff",
                  boxShadow: 1,
                }}
              />
            </Tooltip>
          )}
      </Box>

      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
            {property.name}
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: 0.5, flexShrink: 0 }}>
            {displayPhone && (
              <Tooltip title="Call property">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    stopCardClick(event);
                    setPhoneAnchor(event.currentTarget);
                  }}
                >
                  <PhoneIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {displayWebsite && (
              <Tooltip title="Visit website">
                <IconButton
                  size="small"
                  href={displayWebsite}
                  target="_blank"
                  onClick={stopCardClick}
                >
                  <WebsiteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Copy address">
              <IconButton
                size="small"
                onClick={(event) => {
                  stopCardClick(event);
                  handleCopyAddress();
                }}
              >
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
          noWrap
        >
          <LocationIcon fontSize="small" />
          {property.neighborhood || fullAddress || "Address not available"}
        </Typography>

        {/* Price Range */}
        {priceRangeLabel() && (
          <Typography
            variant="body1"
            fontWeight={700}
            color="primary.main"
            sx={{ mt: 1 }}
          >
            {priceRangeLabel()}/mo
          </Typography>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            mt: 1.5,
          }}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, flex: 1 }}>
            {property.property_management && (
              <Chip
                label={property.property_management}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
              />
            )}
            {displayFloorPlanCount !== null && (
              <Chip
                label={`${displayFloorPlanCount} floor plans`}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.7rem" }}
              />
            )}
            {(property.commission_type && property.commission_with_tour) && (
              <Tooltip
                placement="top"
                title={
                  <>
                    <b>Commission</b><br/>
                    With Tour: {property.commission_type === "flat" ? `$${property.commission_with_tour}` : `${property.commission_with_tour}% of base rent`}
                    {property.commission_without_tour && (
                      <><br/>Without Tour: {property.commission_type === "flat" ? `$${property.commission_without_tour}` : `${property.commission_without_tour}% of base rent`}</>
                    )}
                  </>
                }
              >
                <Chip
                  label={property.commission_type === "flat"
                    ? `$${property.commission_with_tour}`
                    : `${property.commission_with_tour}% Base Rent`
                  }
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem" }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>
      </CardContent>

      {/* Phone popover */}
      <Popover
        open={Boolean(phoneAnchor)}
        anchorEl={phoneAnchor}
        onClose={() => setPhoneAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        onClick={stopCardClick}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="body1" fontWeight={600}>
            {isDemo
              ? "(xxx) xxx-xxxx"
              : displayPhone ? formatPhone(displayPhone) : ""}
          </Typography>
          <Button
            size="small"
            variant="contained"
            color="success"
            startIcon={<PhoneIcon />}
            href={isDemo ? undefined : `tel:${displayPhone}`}
            onClick={() => setPhoneAnchor(null)}
          >
            Call
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CopyIcon />}
            onClick={() => {
              navigator.clipboard.writeText(isDemo ? "(xxx) xxx-xxxx" : displayPhone || "");
              setPhoneCopied(true);
              setTimeout(() => { setPhoneCopied(false); setPhoneAnchor(null); }, 1000);
            }}
          >
            {phoneCopied ? "Copied!" : "Copy"}
          </Button>
        </Box>
      </Popover>

    </Card>
  );
};

// =============================================================================
// Combined Property Modal
// =============================================================================

const EditableTabHeader: React.FC<{
  title: string;
  description: string;
  isEditing: boolean;
  onStartEditing: () => void;
  canEdit?: boolean;
}> = ({ title, description, isEditing, onStartEditing, canEdit = true }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      mb: 3,
      gap: 2,
    }}
  >
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {description}
      </Typography>
    </Box>
    {canEdit && (
      isEditing ? (
        <Chip size="small" color="primary" label="Editing" />
      ) : (
        <Tooltip title="Edit this section">
          <IconButton size="small" onClick={onStartEditing}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    )}
  </Box>
);

const formatFloorPlanPrice = (price: number | null) => {
  if (!price) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(price);
};

const getBedroomLabel = (beds: number | null) => {
  if (beds === null || beds === 0) return "Studio";
  return `${beds} Bed${beds > 1 ? "s" : ""}`;
};

// Unit interface for floor plan detail table
interface FloorPlanUnit {
  id: string;
  floor_plan_id: string;
  unit_number: string | null;
  status: string | null;
  available_date: string | null;
  current_price: number | null;
  square_feet: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
}

const PropertyFloorPlansTab: React.FC<{
  propertyId: string | null;
  propertyName: string;
}> = ({ propertyId, propertyName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expandedImage, setExpandedImage] = useState<{
    open: boolean;
    url: string;
    name: string;
    floorPlanId: string;
  }>({ open: false, url: "", name: "", floorPlanId: "" });

  const { data: floorPlansData = null, isLoading: loading, error: queryError } = useQuery({
    queryKey: ["properties", "floor-plans-tab", propertyId],
    queryFn: () => fetchWithAuth<FloorPlansResponse>(
      `/properties-management/${propertyId}/floor-plans`
    ),
    enabled: !!propertyId,
    staleTime: PROPERTY_STALE_TIME,
    gcTime: PROPERTY_GC_TIME,
  });

  // Fetch units for this property (used in floor plan detail modal)
  const { data: propertyUnits = [] } = useQuery({
    queryKey: ["properties", "units", propertyId],
    queryFn: () => fetchWithAuth<FloorPlanUnit[]>(
      `/search/property/${propertyId}/units`
    ),
    enabled: !!propertyId,
    staleTime: PROPERTY_STALE_TIME,
    gcTime: PROPERTY_GC_TIME,
  });

  const error = queryError ? "Failed to load floor plans." : null;

  const groupedFloorPlans = useMemo(() => {
    const floorPlans = floorPlansData?.floor_plans || [];
    return floorPlans.reduce((groups, floorPlan) => {
      const key =
        floorPlan.bedrooms === null || floorPlan.bedrooms === 0
          ? "Studio"
          : `${floorPlan.bedrooms} Bedroom`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(floorPlan);
      return groups;
    }, {} as Record<string, FloorPlan[]>);
  }, [floorPlansData]);

  const stats = useMemo(() => {
    const floorPlans = floorPlansData?.floor_plans || [];
    if (floorPlans.length === 0) return null;

    const imageCount = floorPlans.filter(
      (floorPlan) => floorPlan.s3_image_url || floorPlan.image_url
    ).length;
    const availableUnits = floorPlans.reduce(
      (sum, floorPlan) => sum + (floorPlan.available_units || 0),
      0
    );
    const minPrice = floorPlans.reduce<number | null>((lowest, floorPlan) => {
      if (floorPlan.min_price === null) return lowest;
      if (lowest === null) return floorPlan.min_price;
      return Math.min(lowest, floorPlan.min_price);
    }, null);
    const maxPrice = floorPlans.reduce<number | null>((highest, floorPlan) => {
      if (floorPlan.max_price === null) return highest;
      if (highest === null) return floorPlan.max_price;
      return Math.max(highest, floorPlan.max_price);
    }, null);

    return {
      imageCount,
      availableUnits,
      minPrice,
      maxPrice,
    };
  }, [floorPlansData]);

  if (loading) {
    return (
      <Box sx={{ py: 8, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!floorPlansData || floorPlansData.floor_plans.length === 0) {
    return (
      <Alert severity="info">
        No floor plans are currently available for {propertyName}.
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            Floor Plans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Browse the current plan mix for {propertyName}.
          </Typography>
        </Box>
        {stats && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              icon={<LayersIcon />}
              label={`${floorPlansData.total_floor_plans} floor plans`}
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<HomeIcon />}
              label={`${stats.availableUnits} available units`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`${stats.imageCount} with images`}
              variant="outlined"
            />
            {stats.minPrice !== null && stats.maxPrice !== null && (
              <Chip
                label={`${formatFloorPlanPrice(
                  stats.minPrice
                )} - ${formatFloorPlanPrice(stats.maxPrice)}`}
                color="warning"
                variant="outlined"
              />
            )}
          </Stack>
        )}
      </Box>

      <Stack spacing={3}>
        {Object.entries(groupedFloorPlans).map(([group, floorPlans]) => (
          <Box key={group}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1.5, textTransform: "uppercase", letterSpacing: 0.6 }}
            >
              {group}
            </Typography>
            <Grid container spacing={2}>
              {floorPlans.map((floorPlan) => {
                const imageUrl =
                  floorPlan.s3_image_url || floorPlan.image_url || null;
                const priceLabel =
                  floorPlan.min_price !== null &&
                  floorPlan.max_price !== null &&
                  floorPlan.min_price !== floorPlan.max_price
                    ? `${formatFloorPlanPrice(
                        floorPlan.min_price
                      )} - ${formatFloorPlanPrice(floorPlan.max_price)}`
                    : formatFloorPlanPrice(
                        floorPlan.min_price ?? floorPlan.max_price
                      );

                return (
                  <Grid item xs={12} md={6} key={floorPlan.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        borderRadius: 3,
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          height: 180,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                        }}
                      >
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={floorPlan.model_name || "Floor plan"}
                            onClick={() =>
                              setExpandedImage({
                                open: true,
                                url: imageUrl,
                                name: floorPlan.model_name || "Floor Plan",
                                floorPlanId: floorPlan.id,
                              })
                            }
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              p: 1.5,
                              bgcolor: alpha(theme.palette.grey[100], 0.7),
                              cursor: "pointer",
                              transition: "opacity 0.15s",
                              "&:hover": { opacity: 0.85 },
                            }}
                          />
                        ) : (
                          <ApartmentIcon
                            sx={{
                              fontSize: 48,
                              color: "text.secondary",
                              opacity: 0.35,
                            }}
                          />
                        )}
                      </Box>
                      <CardContent>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 2,
                            mb: 1.5,
                          }}
                        >
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {floorPlan.model_name || "Unnamed plan"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {getBedroomLabel(floorPlan.bedrooms)}
                              {floorPlan.bathrooms !== null
                                ? ` · ${floorPlan.bathrooms} Bath${
                                    floorPlan.bathrooms > 1 ? "s" : ""
                                  }`
                                : ""}
                              {floorPlan.square_feet
                                ? ` · ${floorPlan.square_feet.toLocaleString()} SF`
                                : ""}
                            </Typography>
                          </Box>
                          <Chip
                            size="small"
                            color={
                              (floorPlan.available_units || 0) > 0
                                ? "success"
                                : "default"
                            }
                            label={`${
                              floorPlan.available_units || 0
                            } units available`}
                          />
                        </Box>
                        <Typography
                          variant="body1"
                          fontWeight={700}
                          color="primary.main"
                        >
                          {priceLabel}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        ))}
      </Stack>

      {/* Floor plan image expansion dialog with unit table */}
      <Dialog
        open={expandedImage.open}
        onClose={() => setExpandedImage({ open: false, url: "", name: "", floorPlanId: "" })}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1, pr: 7, position: "relative" }}><span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => setExpandedImage({ open: false, url: "", name: "", floorPlanId: "" })}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
          {expandedImage.name}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 0 }}>
          {/* Floor plan image */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
            }}
          >
            <img
              src={expandedImage.url}
              alt={expandedImage.name}
              style={{
                maxWidth: "100%",
                maxHeight: "45vh",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </Box>

          {/* Unit data table */}
          {(() => {
            const units = propertyUnits.filter(
              (u) => u.floor_plan_id === expandedImage.floorPlanId
            );
            const availableUnits = units.filter((u) => u.status === "available");
            const allUnits = [...availableUnits, ...units.filter((u) => u.status !== "available")];

            if (allUnits.length === 0) {
              return (
                <Box
                  sx={{
                    py: 3,
                    textAlign: "center",
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    mb: 2,
                  }}
                >
                  <ApartmentIcon sx={{ fontSize: 32, color: "text.disabled", mb: 0.5 }} />
                  <Typography variant="body2" color="text.secondary">
                    No unit data available
                  </Typography>
                </Box>
              );
            }

            return (
              <TableContainer
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                      {!isMobile && (
                        <TableCell sx={{ fontWeight: 600 }}>Bed / Bath</TableCell>
                      )}
                      {!isMobile && (
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          Sq Ft
                        </TableCell>
                      )}
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        Price
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allUnits.map((unit) => {
                      const isAvailable = unit.status === "available";
                      const availDate = unit.available_date
                        ? parseCalendarDate(unit.available_date)
                        : null;
                      const now = new Date();
                      const isAvailableNow = isAvailable && availDate && availDate <= now;

                      return (
                        <TableRow
                          key={unit.id}
                          sx={{
                            "&:last-child td": { borderBottom: 0 },
                          }}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {unit.unit_number || "N/A"}
                            </Typography>
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {unit.bedrooms != null
                                  ? unit.bedrooms === 0
                                    ? "Studio"
                                    : `${unit.bedrooms}bd`
                                  : "—"}
                                {unit.bathrooms != null ? ` / ${unit.bathrooms}ba` : ""}
                              </Typography>
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell align="right">
                              <Typography variant="body2" color="text.secondary">
                                {unit.square_feet
                                  ? `${unit.square_feet.toLocaleString()}`
                                  : "—"}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <Typography fontWeight={700} color="primary.main">
                              {unit.current_price
                                ? `$${unit.current_price.toLocaleString()}`
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={
                                isAvailable
                                  ? isAvailableNow
                                    ? "Available"
                                    : availDate
                                    ? availDate.toLocaleDateString("en-US", {
                                        month: "short",
                                        day: "numeric",
                                      })
                                    : "Available"
                                  : unit.status || "Unknown"
                              }
                              color={
                                isAvailableNow
                                  ? "success"
                                  : isAvailable
                                  ? "info"
                                  : "default"
                              }
                              sx={{
                                fontWeight: 500,
                                borderRadius: 1,
                                height: 24,
                                textTransform: isAvailable ? "none" : "capitalize",
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </DialogContent>

      </Dialog>
    </Box>
  );
};

interface EditPropertyDialogProps {
  open: boolean;
  property: Property | null;
  initialTab: number;
  onClose: () => void;
  onSave: (
    propertyId: string,
    updates: Partial<Property>
  ) => Promise<Property | null>;
  isAdmin?: boolean;
}

const formatFreshnessDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return "Never";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return "Just now";
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

const EditPropertyDialog: React.FC<EditPropertyDialogProps> = ({
  open,
  property,
  initialTab,
  onClose,
  onSave,
  isAdmin = false,
}) => {
  const [formData, setFormData] = useState<Partial<Property>>({});
  const dirtyFields = useRef(new Set<keyof Property>());
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const saveContext = useRef(0);
  useLayoutEffect(() => {
    setSaving(false);
    return () => { saveContext.current += 1; };
  }, [open, property?.id]);
  const previousOpenRef = useRef(false);
  const previousPropertyIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (property && open) {
      const openingDialog = !previousOpenRef.current;
      const switchedProperties = previousPropertyIdRef.current !== property.id;

      if (openingDialog || switchedProperties || !isEditing) {
        dirtyFields.current.clear();
        setFormData(property);
      } else {
        // Refresh untouched fields while retaining only intentional local edits.
        setFormData(previous => {
          const next: Partial<Property> = { ...property };
          dirtyFields.current.forEach(field => { (next as any)[field] = previous[field]; });
          return next;
        });
      }

      if (openingDialog || switchedProperties) {
        setActiveTab(initialTab);
        setIsEditing(false);
      }
    }

    previousOpenRef.current = open;
    previousPropertyIdRef.current = property?.id || null;
  }, [property, initialTab, open, isEditing]);

  useEffect(() => {
    setIsEditing(false);
  }, [activeTab]);

  const handleChange = (field: keyof Property, value: any) => {
    dirtyFields.current.add(field);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (property && isAdmin && !saving) {
      const intended = { ...property };
      dirtyFields.current.forEach(field => { (intended as any)[field] = formData[field]; });
      const payload = buildPropertyUpdatePayload(property, intended);

      if (Object.keys(payload).length === 0) {
        setIsEditing(false);
        return;
      }

      const context = saveContext.current;
      setSaving(true);
      try {
        const savedProperty = await onSave(property.id, payload);
        if (context !== saveContext.current) return;
        if (savedProperty?.id === property.id) {
          setFormData(savedProperty);
          setIsEditing(false);
        }
      } finally {
        if (context === saveContext.current) setSaving(false);
      }
    }
  };

  if (!property) return null;

  return (
    <Drawer
        anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: {xs:"100%", md:900}, maxWidth:"100%", pt:3 } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <ApartmentIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {property.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isAdmin ? "Edit property details" : "Property details · read only"}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {isAdmin && property.last_scraped && (
            <Typography variant="caption" color="text.secondary">
              Data from: {formatFreshnessDate(property.last_scraped)}
            </Typography>
          )}
          <DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={onClose}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip>
        </Box>
      </DialogTitle>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        variant="scrollable" scrollButtons="auto"
        sx={{ px: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Amenities & location" />
        <Tab label="Availability" />
        <Tab label="Money" />
        <Tab label="Special & fees" />
        <Tab label="Details" />
        <Tab label="Contacts" />
        <Tab label="Activity" />
        <Tab label="Reviews" />
      </Tabs>

      <DialogContent sx={{ mt: 2 }}>
        {activeTab === 5 && <PropertyContacts propertyId={property.id} canEdit={isAdmin} />}
        {activeTab === 6 && <PropertyActivity propertyId={property.id} />}
        {activeTab === 7 && <Box><Typography variant="h6">Reviews</Typography><Button href={googleReviewsUrl(property)} target="_blank" rel="noopener noreferrer">Open Google listing and reviews</Button><Typography>Stored rating: {property.rating ?? "No data yet"} · {property.review_count ?? "Unknown"} reviews. Source and read date unknown.</Typography></Box>}
        {activeTab === PROPERTY_DIALOG_TAB.overview && property && (
          <PropertyEnrichmentPanel property={property} />
        )}
        {activeTab === PROPERTY_DIALOG_TAB.floorPlans && (
          <><Typography variant="caption">As of {property.last_scraped ? formatFreshnessDate(property.last_scraped) : "unknown — refresh needed"}</Typography><PropertyFloorPlansTab
            propertyId={property.id}
            propertyName={property.name}
          /></>
        )}

        {activeTab === PROPERTY_DIALOG_TAB.commissionNotes && (
          <Stack spacing={3}>
            <EditableTabHeader
              title="Current stated terms & notes"
              description="Property terms as recorded. Their source and effective date are not tracked here; confirm they are current."
              isEditing={isEditing}
              onStartEditing={() => setIsEditing(true)}
              canEdit={isAdmin}
            />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Commission type" value={formData.commission_type ?? ""}
                  disabled={!isEditing || saving} onChange={e => handleChange("commission_type", e.target.value || null)}>
                  <MenuItem value="">Not recorded</MenuItem>
                  <MenuItem value="percentage">Percent of base rent</MenuItem>
                  <MenuItem value="flat">Flat amount (USD)</MenuItem>
                  {formData.commission_type && !["percentage", "flat"].includes(formData.commission_type) && (
                    <MenuItem value={formData.commission_type}>{formData.commission_type} (as recorded)</MenuItem>
                  )}
                </TextField>
              </Grid>
              {(["commission_with_tour", "commission_without_tour"] as const).map(field => (
                <Grid item xs={12} sm={4} key={field}>
                  <TextField fullWidth type="number" label={field === "commission_with_tour" ? "With tour" : "Without tour"}
                    value={formData[field] ?? ""} disabled={!isEditing || saving} inputProps={{ step: "any" }}
                    helperText={formData.commission_type === "flat" ? "USD" : formData.commission_type === "percentage" ? "% of base rent" : "Value in the recorded term type"}
                    onChange={e => handleChange(field, e.target.value === "" ? null : Number(e.target.value))} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <TextField fullWidth multiline minRows={3} label="Property notes" value={formData.notes ?? ""}
                  disabled={!isEditing || saving} onChange={e => handleChange("notes", e.target.value || null)} />
              </Grid>
            </Grid>
            <Divider />
            <Typography variant="subtitle1" fontWeight={700}>Recorded invoice outcomes</Typography>
            <PropertyMoney propertyId={property.id} />
          </Stack>
        )}
        {activeTab === PROPERTY_DIALOG_TAB.specialsFees && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <EditableTabHeader
                title="Specials & Fees"
                description="Manage specials, pet policy, and fees for this property."
                isEditing={isEditing}
                onStartEditing={() => setIsEditing(true)}
                canEdit={isAdmin}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Specials Summary (card tag)"
                value={isEditing ? formData.specials_summary || "" : property.current_special?.text || "No verified current special"}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange("specials_summary", e.target.value || null)
                }
                placeholder="e.g., Up to 6 Weeks Free"
                helperText="Short text shown on property card"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Full Specials"
                multiline
                rows={3}
                value={isEditing ? formData.specials || "" : property.current_special ? `Ends ${property.current_special.ends_on} · ${property.current_special.text}` : "Expired and undated offers are hidden."}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange("specials", e.target.value || null)
                }
                placeholder="e.g., 2 months free on 13+ month leases, $500 off first month..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Pet Policy"
                multiline
                rows={2}
                value={formData.pet_policy_details || formData.pet_policy || ""}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange("pet_policy_details", e.target.value || null)
                }
                placeholder="e.g., Dogs and cats welcome. $400 deposit, $25/month pet rent..."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pet Deposit ($)"
                type="number"
                value={formData.pet_deposit ?? formData.pet_fee ?? ""}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange("pet_deposit", e.target.value ? Number(e.target.value) : null)
                }
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Admin Fee ($)"
                type="number"
                value={formData.admin_fee || ""}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange("admin_fee", e.target.value ? Number(e.target.value) : null)
                }
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Application Fee ($)"
                type="number"
                value={formData.application_fee || ""}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange("application_fee", e.target.value ? Number(e.target.value) : null)
                }
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
          </Grid>
        )}

        {activeTab === PROPERTY_DIALOG_TAB.details && (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <EditableTabHeader
                title="Details"
                description="Contact info, address, and property details."
                isEditing={isEditing}
                onStartEditing={() => setIsEditing(true)}
                canEdit={isAdmin}
              />
            </Grid>
            {/* Contact Info */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Phone"
                value={formData.phone || formData.contact_phone || ""}
                disabled={!isEditing || saving}
                onChange={(e) => handleChange("phone", e.target.value || null)}
                InputProps={{ startAdornment: <InputAdornment position="start"><PhoneIcon /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact Email"
                value={formData.contact_email || ""}
                disabled={!isEditing || saving}
                onChange={(e) => handleChange("contact_email", e.target.value || null)}
                InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Website"
                value={formData.property_website || formData.property_link || formData.website || ""}
                disabled={!isEditing || saving}
                onChange={(e) => handleChange("property_website", e.target.value || null)}
                InputProps={{ startAdornment: <InputAdornment position="start"><WebsiteIcon /></InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            {/* Address & Details */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address || ""}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange("address", e.target.value || null)
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                value={formData.city || ""}
                disabled={!isEditing || saving}
                onChange={(e) => handleChange("city", e.target.value || null)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                value={formData.state || ""}
                disabled={!isEditing || saving}
                onChange={(e) => handleChange("state", e.target.value || null)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="ZIP Code"
                value={formData.zip_code || ""}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange("zip_code", e.target.value || null)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Neighborhood"
                value={formData.neighborhood || ""}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange("neighborhood", e.target.value || null)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Units"
                type="number"
                value={formData.total_units || ""}
                disabled={!isEditing || saving}
                onChange={(e) =>
                  handleChange(
                    "total_units",
                    e.target.value ? Number(e.target.value) : null
                  )
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.active ?? true}
                    disabled={!isEditing || saving}
                    onChange={(e) => handleChange("active", e.target.checked)}
                  />
                }
                label="Property is active"
              />
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        {isEditing ? (
          <>
            <Button
              disabled={saving}
              onClick={() => {
                setFormData(property);
                setIsEditing(false);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              Save Changes
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Drawer>
  );
};

// =============================================================================
// Filter Drawer
// =============================================================================

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  neighborhoods: string[];
  managementCompanies: string[];
}

const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  filters,
  onFilterChange,
  neighborhoods,
  managementCompanies,
}) => {
  const handleNeighborhoodToggle = (neighborhood: string) => {
    const updated = filters.neighborhoods.includes(neighborhood)
      ? filters.neighborhoods.filter((n) => n !== neighborhood)
      : [...filters.neighborhoods, neighborhood];
    onFilterChange({ ...filters, neighborhoods: updated });
  };

  const handleManagementToggle = (company: string) => {
    const updated = filters.propertyManagement.includes(company)
      ? filters.propertyManagement.filter((c) => c !== company)
      : [...filters.propertyManagement, company];
    onFilterChange({ ...filters, propertyManagement: updated });
  };

  const clearFilters = () => {
    onFilterChange({
      search: "",
      neighborhoods: [],
      propertyManagement: [],
      hasPetPolicy: null,
      hasSpecials: null,
      hasCommission: null,
      showFavoritesOnly: false,
      showActiveOnly: true,
      noFloorPlanImage: false,
    });
  };

  const activeFilterCount =
    filters.neighborhoods.length +
    filters.propertyManagement.length +
    (filters.hasPetPolicy !== null ? 1 : 0) +
    (filters.hasSpecials !== null ? 1 : 0) +
    (filters.hasCommission !== null ? 1 : 0) +
    (filters.showFavoritesOnly ? 1 : 0) +
    (filters.noFloorPlanImage ? 1 : 0);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 360, p: 3 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Filters
          {activeFilterCount > 0 && (
            <Chip
              label={activeFilterCount}
              size="small"
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Typography>
        <DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={onClose}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip>
      </Box>

      {activeFilterCount > 0 && (
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={clearFilters}
          sx={{ mb: 3 }}
        >
          Clear All Filters
        </Button>
      )}

      {/* Quick Filters */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        QUICK FILTERS
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={filters.showFavoritesOnly}
              onChange={(e) =>
                onFilterChange({
                  ...filters,
                  showFavoritesOnly: e.target.checked,
                })
              }
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StarIcon sx={{ color: "warning.main" }} fontSize="small" />
              Favorites only
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Switch
              checked={filters.hasSpecials === true}
              onChange={(e) =>
                onFilterChange({
                  ...filters,
                  hasSpecials: e.target.checked ? true : null,
                })
              }
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SpecialIcon sx={{ color: "warning.main" }} fontSize="small" />
              Has specials
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Switch
              checked={filters.hasCommission === true}
              onChange={(e) =>
                onFilterChange({
                  ...filters,
                  hasCommission: e.target.checked ? true : null,
                })
              }
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CommissionIcon sx={{ color: "success.main" }} fontSize="small" />
              Pays commission
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Switch
              checked={filters.hasPetPolicy === true}
              onChange={(e) =>
                onFilterChange({
                  ...filters,
                  hasPetPolicy: e.target.checked ? true : null,
                })
              }
            />
          }
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <PetsIcon fontSize="small" />
              Pet friendly
            </Box>
          }
        />
        <FormControlLabel
          control={
            <Switch
              checked={filters.showActiveOnly}
              onChange={(e) =>
                onFilterChange({ ...filters, showActiveOnly: e.target.checked })
              }
            />
          }
          label="Active properties only"
        />
        <FormControlLabel
          control={
            <Switch
              checked={filters.noFloorPlanImage}
              onChange={(e) =>
                onFilterChange({ ...filters, noFloorPlanImage: e.target.checked })
              }
            />
          }
          label="No floor plan image"
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Neighborhoods */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        NEIGHBORHOODS ({neighborhoods.length})
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          mb: 3,
        }}
      >
        {neighborhoods.map((neighborhood) => (
          <Chip
            key={neighborhood}
            label={neighborhood}
            size="small"
            variant={
              filters.neighborhoods.includes(neighborhood)
                ? "filled"
                : "outlined"
            }
            color={
              filters.neighborhoods.includes(neighborhood)
                ? "primary"
                : "default"
            }
            onClick={() => handleNeighborhoodToggle(neighborhood)}
          />
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Management Companies */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        MANAGEMENT COMPANIES ({managementCompanies.length})
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
        {managementCompanies.map((company) => (
          <Chip
            key={company}
            label={company}
            size="small"
            variant={
              filters.propertyManagement.includes(company)
                ? "filled"
                : "outlined"
            }
            color={
              filters.propertyManagement.includes(company)
                ? "primary"
                : "default"
            }
            onClick={() => handleManagementToggle(company)}
          />
        ))}
      </Box>
    </Drawer>
  );
};

// =============================================================================
// Stats Card Component
// =============================================================================

interface StatCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  onClick?: () => void;
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  subtitle,
  onClick,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const activeOnClick = loading ? undefined : onClick;
  const clickSx = activeOnClick
    ? { cursor: "pointer", "&:hover": { borderColor: alpha(color, 0.4) } }
    : {};

  if (isMobile) {
    return (
      <Card sx={{ p: 1.5, height: "100%", ...clickSx }} onClick={activeOnClick}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ color, display: "flex", "& svg": { fontSize: 20 } }}>
            {icon}
          </Box>
          {loading ? (
            <Skeleton animation="wave" width={36} height={28} />
          ) : (
            <Typography variant="h6" fontWeight={700} sx={{ color }}>
              {value.toLocaleString()}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 2.5, height: "100%", ...clickSx }} onClick={activeOnClick}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box>
          {loading ? (
            <Skeleton animation="wave" width={76} height={48} />
          ) : (
            <Typography variant="h3" fontWeight={700} sx={{ color }}>
              {value.toLocaleString()}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar
          sx={{ bgcolor: alpha(color, 0.1), color, width: 48, height: 48 }}
        >
          {icon}
        </Avatar>
      </Box>
    </Card>
  );
};

// =============================================================================
// Main Properties Page Component
// =============================================================================

const PropertiesPage: React.FC<{
  isAdmin?: boolean;
  isDemo?: boolean;
  userRole?: "agent" | "admin" | "manager" | "demo";
  initialPropertyId?: string | null;
  initialPropertyRequestKey?: number;
  onNavigate?: (page: string) => void;
}> = ({
  isAdmin = false,
  isDemo = false,
  userRole = "agent",
  initialPropertyId = null,
  initialPropertyRequestKey = 0,
  onNavigate,
}) => {
  const initialUrlState = useMemo(
    () => parseAppNavigationState(window.location.search).properties,
    []
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const queryClient = useQueryClient();
  const {
    data: rawProperties = [],
    isLoading: loading,
    isFetching: refreshing,
    error: propertiesError,
  } = usePropertiesEnriched();
  const commissions = useQuery({queryKey:['property-commissions'], queryFn:()=>fetchWithAuth<Record<string,LearnedCommission|null>>('/properties-management/history/commissions'), enabled:!isDemo});
  const properties = useMemo(()=>rawProperties.map(p=>({...p, learned_commission:commissions.data?.[p.id]})),[rawProperties,commissions.data]);
  const { data: stats = null } = usePropertyStats();
  const { data: neighborhoods = [] } = useNeighborhoods();
  const { data: managementCompanies = [] } = useManagementCompanies();
  const { invalidateAll, invalidateProperty } = usePropertyInvalidation();
  const [viewMode, setViewMode] = usePersistedState<"grid" | "list">(
    "properties.viewMode",
    initialUrlState?.view ?? "grid",
    { validate: (value) => value === "grid" || value === "list" }
  );
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [propertyDeepLinkPending, setPropertyDeepLinkPending] = useState(
    () => Boolean(initialUrlState?.selectedPropertyId || initialPropertyId)
  );
  const handledPropertyRequestRef = useRef<string | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const [dialogInitialTab, setDialogInitialTab] = useState<number>(
    PROPERTY_DIALOG_TAB.overview
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const [specialsDialogOpen, setSpecialsDialogOpen] = useState(false);
  const [commissionDialogOpen, setCommissionDialogOpen] = useState(false);
  const [agentInsightsOpen, setAgentInsightsOpen] = useState(false);
  const [insightsIncludeAvailableNow, setInsightsIncludeAvailableNow] =
    useState(false);
  const [insightsSelectedDate, setInsightsSelectedDate] = useState<string | null>(
    null
  );
  const [insightsUnitsOpen, setInsightsUnitsOpen] = useState(false);
  const [insightsSelectedUnitId, setInsightsSelectedUnitId] = useState<
    string | null
  >(null);
  const [insightsUnitDetailOpen, setInsightsUnitDetailOpen] = useState(false);

  const capabilities = React.useMemo(
    () => buildCapabilities({ role: userRole, isDemo, isMobile }),
    [userRole, isDemo, isMobile]
  );
  const showAgentInsightsButton = capabilities.canOpenInsightsFromProperties;
  // Hide mobile stats row on mobile for all production roles to reduce clutter
  // (previously coupled to the agent-only Insights button — now decoupled so
  // enabling admin Insights doesn't accidentally hide admin mobile stats.)
  const showPropertiesStatsRow = !capabilities.hideMobileSummaryStats;

  const { data: insightsTimelineData, isLoading: insightsTimelineLoading } =
    useAvailabilityTimeline(
      undefined,
      90,
      insightsIncludeAvailableNow,
      {
        enabled: showAgentInsightsButton && agentInsightsOpen,
      }
    );
  const { data: insightsUnitsByDate, isLoading: insightsUnitsByDateLoading } =
    useUnitsByDate(insightsSelectedDate, undefined, {
      enabled:
        showAgentInsightsButton && agentInsightsOpen && Boolean(insightsSelectedDate),
    });
  const { data: insightsSelectedUnit, isLoading: insightsSelectedUnitLoading } =
    useUnitDetail(insightsSelectedUnitId, {
      enabled:
        showAgentInsightsButton &&
        agentInsightsOpen &&
        Boolean(insightsSelectedUnitId),
    });

  // Build specials data from already-loaded properties (no API call needed)
  const specialsData = useMemo(
    () =>
      properties
        .filter(p => Boolean(p.current_special))
        .map((p) => ({
          property_id: p.id,
          property_name: p.name || "Unknown",
          neighborhood: p.neighborhood || null,
          specials_summary: p.current_special!.text,
          ends_on: p.current_special!.ends_on,
          captured_at: p.current_special!.captured_at,
          primary_image_url: p.s3_image_url || null,
        })),
    [properties]
  );
  const specialsLoading = loading;

  const [filters, setFilters] = usePersistedState<FilterState>(
    "properties.filters",
    {
      search: initialUrlState?.search ?? "",
      neighborhoods: initialUrlState?.neighborhoods ?? [],
      propertyManagement: initialUrlState?.management ?? [],
      hasPetPolicy: initialUrlState?.flags.includes("pet") ? true : null,
      hasSpecials: initialUrlState?.flags.includes("specials") ? true : null,
      hasCommission: initialUrlState?.flags.includes("commission") ? true : null,
      showFavoritesOnly: initialUrlState?.flags.includes("favorites") ?? false,
      showActiveOnly: !initialUrlState?.flags.includes("inactive"),
      noFloorPlanImage: initialUrlState?.flags.includes("no-image") ?? false,
    },
    { validate: isFilterState }
  );

  const urlHydratedRef = useRef(false);
  useEffect(() => {
    if (urlHydratedRef.current || !initialUrlState) return;
    urlHydratedRef.current = true;
    setViewMode(initialUrlState.view);
    setFilters((previous) => ({
      ...previous,
      search: initialUrlState.search,
      neighborhoods: initialUrlState.neighborhoods,
      propertyManagement: initialUrlState.management,
      hasPetPolicy: initialUrlState.flags.includes("pet") ? true : null,
      hasSpecials: initialUrlState.flags.includes("specials") ? true : null,
      hasCommission: initialUrlState.flags.includes("commission") ? true : null,
      showFavoritesOnly: initialUrlState.flags.includes("favorites"),
      showActiveOnly: !initialUrlState.flags.includes("inactive"),
      noFloorPlanImage: initialUrlState.flags.includes("no-image"),
    }));
  }, [initialUrlState, setFilters, setViewMode]);

  useEffect(() => {
    const onPopState = () => {
      const restored = resolvePropertyNavigationState(
        parseAppNavigationState(window.location.search).properties
      );
      setViewMode(restored.view);
      setFilters((previous) => ({ ...previous, search: restored.search,
        neighborhoods: restored.neighborhoods, propertyManagement: restored.management,
        hasPetPolicy: restored.flags.includes("pet") ? true : null,
        hasSpecials: restored.flags.includes("specials") ? true : null,
        hasCommission: restored.flags.includes("commission") ? true : null,
        showFavoritesOnly: restored.flags.includes("favorites"),
        showActiveOnly: !restored.flags.includes("inactive"),
        noFloorPlanImage: restored.flags.includes("no-image") }));
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [setFilters, setViewMode]);

  useEffect(() => {
    const flags = [
      filters.hasPetPolicy ? "pet" : null,
      filters.hasSpecials ? "specials" : null,
      filters.hasCommission ? "commission" : null,
      filters.showFavoritesOnly ? "favorites" : null,
      !filters.showActiveOnly ? "inactive" : null,
      filters.noFloorPlanImage ? "no-image" : null,
    ].filter((value): value is string => Boolean(value));
    const selectedPropertyId = resolveSelectedPropertyForWrite(
      propertyDeepLinkPending || Boolean(initialPropertyId && handledPropertyRequestRef.current !== initialPropertyId),
      initialUrlState?.selectedPropertyId ?? initialPropertyId ?? null,
      editDialogOpen,
      selectedProperty?.id ?? null
    );
    const isCanonicalDefault =
      viewMode === "grid" && filters.search === "" &&
      filters.neighborhoods.length === 0 &&
      filters.propertyManagement.length === 0 &&
      filters.hasPetPolicy === null && filters.hasSpecials === null &&
      filters.hasCommission === null && !filters.showFavoritesOnly &&
      filters.showActiveOnly && !filters.noFloorPlanImage &&
      selectedPropertyId === null;
    replaceNavigationState({ properties: isCanonicalDefault ? null : {
      view: viewMode,
      search: "",
      neighborhoods: filters.neighborhoods,
      management: filters.propertyManagement,
      flags,
      selectedPropertyId,
    }});
  }, [viewMode, filters, editDialogOpen, selectedProperty?.id, propertyDeepLinkPending,
    initialUrlState?.selectedPropertyId, initialPropertyId]);


  const [overviewSlice,setOverviewSlice]=useState(()=>{
    const p=new URLSearchParams(window.location.search);const max=Number(p.get('overviewMaxRent'));
    return {available:p.get('overviewAvailable')==='true',maxRent:Number.isFinite(max)&&max>0?max:null};
  });
  const clearOverviewSlice=()=>{const url=new URL(window.location.href);url.searchParams.delete('overviewAvailable');url.searchParams.delete('overviewMaxRent');window.history.replaceState(window.history.state,'',url.toString());setOverviewSlice({available:false,maxRent:null});};
  // Filter properties
  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      if(overviewSlice.available && !(property.available_units && property.available_units>0)) return false;
      const maxRent=overviewSlice.maxRent || 0;
      if(maxRent>0 && !(property.min_price && property.min_price<=maxRent)) return false;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          property.name.toLowerCase().includes(searchLower) ||
          property.address?.toLowerCase().includes(searchLower) ||
          property.neighborhood?.toLowerCase().includes(searchLower) ||
          property.property_management?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      if (
        filters.neighborhoods.length > 0 &&
        !filters.neighborhoods.includes(property.neighborhood || "Unknown")
      ) {
        return false;
      }

      if (
        filters.propertyManagement.length > 0 &&
        !filters.propertyManagement.includes(property.property_management || "")
      ) {
        return false;
      }

      if (filters.hasPetPolicy === true && !getDisplayPetPolicy(property))
        return false;
      if (filters.hasSpecials === true && !property.current_special) return false;
      if (filters.hasCommission === true && !(Number(property.learned_commission?.value)>0 || (property.commission_type && property.commission_with_tour)))
        return false;
      if (filters.showFavoritesOnly && !property.is_favorite) return false;
      if (filters.showActiveOnly && !property.active) return false;
      if (filters.noFloorPlanImage && !(property.floor_plans_missing_images && property.floor_plans_missing_images > 0)) return false;

      return true;
    }).sort((a, b) => {
      // Favorites first
      if (a.is_favorite && !b.is_favorite) return -1;
      if (!a.is_favorite && b.is_favorite) return 1;
      return 0;
    });
  }, [properties, filters, overviewSlice]);

  // Calculate total available units
  const totalAvailableUnits = useMemo(() => {
    return properties.reduce((sum, p) => sum + (p.available_units || 0), 0);
  }, [properties]);

  const avgStartingRent = useMemo(() => {
    const prices = properties
      .map((property) => property.min_price)
      .filter((price): price is number => typeof price === "number" && price > 0);
    if (!prices.length) return null;
    return Math.round(prices.reduce((sum, value) => sum + value, 0) / prices.length);
  }, [properties]);

  const insightWatchlist = useMemo(() => {
    return properties
      .filter((property) => (property.available_units || 0) > 0)
      .sort((a, b) => {
        const availabilityDiff = (b.available_units || 0) - (a.available_units || 0);
        if (availabilityDiff !== 0) return availabilityDiff;
        return (a.min_price || Number.MAX_SAFE_INTEGER) - (b.min_price || Number.MAX_SAFE_INTEGER);
      })
      .slice(0, 6);
  }, [properties]);

  const handleOpenPropertyDialog = (
    property: Property,
    initialTab: number = PROPERTY_DIALOG_TAB.overview
  ) => {
    setSelectedProperty(property);
    setDialogInitialTab(initialTab);
    setEditDialogOpen(true);
  };

  const handleCloseAgentInsights = useCallback(() => {
    setAgentInsightsOpen(false);
    setInsightsSelectedDate(null);
    setInsightsUnitsOpen(false);
    setInsightsSelectedUnitId(null);
    setInsightsUnitDetailOpen(false);
  }, []);

  const handleInsightsTimelineBarClick = useCallback((dateKey: string) => {
    if (!dateKey) {
      setInsightsSelectedDate(null);
      setInsightsUnitsOpen(false);
      return;
    }
    setInsightsSelectedDate(dateKey);
    setInsightsUnitsOpen(true);
  }, []);

  const handleInsightsUnitClick = useCallback((unit: UnitDetail) => {
    setInsightsSelectedUnitId(unit.id);
    setInsightsUnitDetailOpen(true);
  }, []);

  const handleInsightsPropertyClick = useCallback(
    (propertyId: string) => {
      const property = properties.find((item) => item.id === propertyId);
      if (!property) return;
      handleOpenPropertyDialog(property, PROPERTY_DIALOG_TAB.floorPlans);
      setInsightsUnitsOpen(false);
      setInsightsUnitDetailOpen(false);
      setInsightsSelectedUnitId(null);
      setAgentInsightsOpen(false);
    },
    [properties, handleOpenPropertyDialog]
  );

  useEffect(() => {
    if (!initialPropertyId || !properties.length) return;

    const requestedProperty = properties.find(
      (property) => property.id === initialPropertyId
    );

    if (requestedProperty) {
      handleOpenPropertyDialog(
        requestedProperty,
        PROPERTY_DIALOG_TAB.floorPlans
      );
    }
    handledPropertyRequestRef.current = initialPropertyId;
    setPropertyDeepLinkPending(false);
  }, [initialPropertyId, initialPropertyRequestKey, properties]);

  const handleSave = async (
    propertyId: string,
    updates: Partial<Property>
  ) => {
    try {
      const result = await updateProperty(propertyId, updates);
      if (result) {
        // Optimistic cache update
        queryClient.setQueryData(propertyKeys.enriched, (old: Property[] | undefined) =>
          old?.map((p) => (p.id === propertyId ? { ...p, ...result } : p)) ?? []
        );
        setSelectedProperty((prev) =>
          prev && prev.id === propertyId ? { ...prev, ...result } : prev
        );
        invalidateProperty(propertyId);
        setSnackbar({
          open: true,
          message: "Property updated!",
          severity: "success",
        });
        return result;
      }
    } catch (err) {
      console.error("Failed to update property", err);
      const message =
        err instanceof Error ? err.message : "Failed to update property";
      setSnackbar({
        open: true,
        message,
        severity: "error",
      });
    }
    return null;
  };

  const handleToggleFavorite = async (id: string) => {
    const property = properties.find((p) => p.id === id);
    if (!property) return;

    const newFavoriteStatus = !property.is_favorite;
    // Optimistic cache update
    queryClient.setQueryData(propertyKeys.enriched, (old: Property[] | undefined) =>
      old?.map((p) => (p.id === id ? { ...p, is_favorite: newFavoriteStatus } : p)) ?? []
    );

    try {
      const result = await toggleFavorite(id);
      if (!result) throw new Error("Toggle failed");
      invalidateProperty(id);
    } catch {
      // Revert optimistic update
      queryClient.setQueryData(propertyKeys.enriched, (old: Property[] | undefined) =>
        old?.map((p) => (p.id === id ? { ...p, is_favorite: !newFavoriteStatus } : p)) ?? []
      );
      setSnackbar({
        open: true,
        message: "Failed to update favorite",
        severity: "error",
      });
    }
  };

  const activeFilterCount =
    filters.neighborhoods.length +
    filters.propertyManagement.length +
    (filters.hasPetPolicy !== null ? 1 : 0) +
    (filters.hasSpecials !== null ? 1 : 0) +
    (filters.hasCommission !== null ? 1 : 0) +
    (filters.showFavoritesOnly ? 1 : 0);

  const propertiesErrorMessage =
    propertiesError instanceof Error
      ? propertiesError.message
      : "Failed to load properties";

  return (
    <Box>
      {/* Header */}
      <PropertiesSectionHeader
        context={!loading && !propertiesError ? [
          `${properties.length} buildings`,
          properties.every(p => typeof p.available_units === "number")
            ? `${properties.filter(p => (p.available_units || 0) > 0).length} with availability` : null,
          (() => {
            const latest = properties.map(p => p.updated_at).filter(Boolean).sort().pop();
            const age = propertyUpdateAge(latest);
            return age ? `latest update ${age}` : null;
          })(),
        ].filter(Boolean).join(' · ') : null}
        actions={<>
          {showAgentInsightsButton && <Button size="small" onClick={() => {
            setAgentInsightsOpen(true); setInsightsUnitDetailOpen(false); setInsightsSelectedUnitId(null);
          }} data-testid="properties-availability-button">Availability</Button>}
          <PageRefreshButton iconOnly loading={refreshing} onClick={() => invalidateAll()} />
        </>}
      />

      {/* Loading bar moved to render with the card scaffold (PageLoadingScaffold
          owns the top-bar render now) so it sits above content, not header
          (Grant directive 2026-04-25 — "I LIKE THE LOWER ONE"). */}

      {/* Stats Row — only show the error once the query has settled (not mid-retry) */}
      {propertiesError && !refreshing && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => invalidateAll()}
            >
              Retry
            </Button>
          }
        >
          Couldn't load properties. {propertiesErrorMessage}
        </Alert>
      )}

      {showPropertiesStatsRow && (
        <Grid container spacing={1} sx={{ mb: { xs: 2, sm: 4 } }}>
          <Grid item xs={12} sm={3}>
            <StatCard
              title="Active Properties"
              value={propertiesError ? "Couldn't load" : properties.length}
              icon={<ApartmentIcon />}
              color={theme.palette.primary.main}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatCard
              title="Available Units"
              value={propertiesError ? "Couldn't load" : totalAvailableUnits}
              icon={<AvailableIcon />}
              color={theme.palette.success.main}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatCard
              title="With Specials"
              value={propertiesError ? "Couldn't load" : properties.filter(p=>p.current_special).length}
              icon={<SpecialIcon />}
              color={theme.palette.warning.main}
              onClick={() => setSpecialsDialogOpen(true)}
              loading={loading}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <StatCard
              title="Pay Commission"
              value={propertiesError || commissions.error ? "Couldn't load" : properties.filter(p=>Number(p.learned_commission?.value)>0 || Number(p.commission_with_tour)>0 || Number(p.commission_without_tour)>0).length}
              icon={<CommissionIcon />}
              color={theme.palette.success.dark}
              onClick={() => setCommissionDialogOpen(true)}
              loading={loading}
            />
          </Grid>
        </Grid>
      )}

      {/* Search and Filter Bar */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            placeholder="Search properties..."
            value={filters.search}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, search: e.target.value }))
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: filters.search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() =>
                      setFilters((prev) => ({ ...prev, search: "" }))
                    }
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 250 }}
          />

          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => setFilterDrawerOpen(true)}
            >
              Filters
            </Button>
          </Badge>

          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            <Tooltip title="Grid view">
              <IconButton
                onClick={() => setViewMode("grid")}
                color={viewMode === "grid" ? "primary" : "default"}
              >
                <GridViewIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="List view">
              <IconButton
                onClick={() => setViewMode("list")}
                color={viewMode === "list" ? "primary" : "default"}
              >
                <ListViewIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Active filters display */}
        {activeFilterCount > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            {filters.neighborhoods.map((n) => (
              <Chip
                key={n}
                label={n}
                size="small"
                onDelete={() =>
                  setFilters((prev) => ({
                    ...prev,
                    neighborhoods: prev.neighborhoods.filter((x) => x !== n),
                  }))
                }
              />
            ))}
            {filters.propertyManagement.map((c) => (
              <Chip
                key={c}
                label={c}
                size="small"
                color="primary"
                onDelete={() =>
                  setFilters((prev) => ({
                    ...prev,
                    propertyManagement: prev.propertyManagement.filter(
                      (x) => x !== c
                    ),
                  }))
                }
              />
            ))}
            {filters.showFavoritesOnly && (
              <Chip
                label="Favorites"
                size="small"
                icon={<StarIcon />}
                onDelete={() =>
                  setFilters((prev) => ({ ...prev, showFavoritesOnly: false }))
                }
              />
            )}
            {filters.hasSpecials && (
              <Chip
                label="Has Specials"
                size="small"
                color="warning"
                onDelete={() =>
                  setFilters((prev) => ({ ...prev, hasSpecials: null }))
                }
              />
            )}
            {filters.hasCommission && (
              <Chip
                label="Pays Commission"
                size="small"
                color="success"
                onDelete={() =>
                  setFilters((prev) => ({ ...prev, hasCommission: null }))
                }
              />
            )}
            {filters.hasPetPolicy && (
              <Chip
                label="Pet Friendly"
                size="small"
                icon={<PetsIcon />}
                onDelete={() =>
                  setFilters((prev) => ({ ...prev, hasPetPolicy: null }))
                }
              />
            )}
          </Box>
        )}
      </Card>

      {/* Results Count */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {loading
            ? "Loading properties…"
            : `Showing ${filteredProperties.length} of ${properties.length} properties`}
        </Typography>
      </Box>

      {/* Loading State — scaffold rendered by primitive (no children;
          existing {!loading && ...} blocks below render content). */}
      <PageLoadingScaffold
        loading={loading}
        refreshing={refreshing}
        variant="card-grid"
        rows={6}
      />

      {commissions.error && <Alert severity="warning" action={<Button onClick={()=>commissions.refetch()}>Retry</Button>}>Couldn't load invoice commission history.</Alert>}
      {(overviewSlice.available || overviewSlice.maxRent) && <Chip sx={{mb:2}} onDelete={clearOverviewSlice} label={overviewSlice.available?'Available now':`Starting rent at or below $${overviewSlice.maxRent}`} />}
      {/* Properties Grid/List. Only when there is something to show: an EMPTY
          MUI Grid container keeps its -24px spacing margin at 0 height and
          pulled the empty-state card up over "Showing N of N" (F29, measured). */}
      {!loading && filteredProperties.length > 0 &&
        (viewMode === "grid" ? (
          <Grid container spacing={3}>
            {filteredProperties.map((property) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={property.id}>
                <PropertyCard
                  property={property}
                  onOpen={handleOpenPropertyDialog}
                  onToggleFavorite={handleToggleFavorite}
                  viewMode={viewMode}
                  isDemo={isDemo}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box>
            {filteredProperties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                onOpen={handleOpenPropertyDialog}
                onToggleFavorite={handleToggleFavorite}
                viewMode={viewMode}
                isDemo={isDemo}
              />
            ))}
          </Box>
        ))}

      {/* Empty State */}
      {!loading && !propertiesError && filteredProperties.length === 0 && (
        <EmptyState
          icon={<ApartmentIcon />}
          title="No properties found"
          description="Try adjusting your filters or search criteria"
          action={<Button
            variant="outlined"
            onClick={() =>
              setFilters({
                search: "",
                neighborhoods: [],
                propertyManagement: [],
                hasPetPolicy: null,
                hasSpecials: null,
                hasCommission: null,
                showFavoritesOnly: false,
                showActiveOnly: true,
                noFloorPlanImage: false,
              })
            }
          >
            Clear All Filters
          </Button>}
        />
      )}

      {/* Dialogs & Drawers */}
      <FilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        filters={filters}
        onFilterChange={setFilters}
        neighborhoods={neighborhoods}
        managementCompanies={managementCompanies}
      />

      <EditPropertyDialog
        open={editDialogOpen}
        property={selectedProperty}
        initialTab={dialogInitialTab}
        onClose={() => setEditDialogOpen(false)}
        onSave={handleSave}
        isAdmin={isAdmin}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Specials Dialog */}
      <Dialog
        open={specialsDialogOpen}
        onClose={() => setSpecialsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 7, position: "relative" }}><span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => setSpecialsDialogOpen(false)}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SpecialIcon sx={{ color: theme.palette.warning.main }} />
            <Typography variant="h6" fontWeight={700}>
              Active Specials
            </Typography>
            {specialsData && (
              <Chip
                label={specialsData.length}
                size="small"
                sx={{
                  ml: "auto",
                  fontWeight: 600,
                  backgroundColor: alpha(theme.palette.warning.main, 0.1),
                  color: theme.palette.warning.main,
                }}
              />
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 2, maxHeight: 500, overflow: "auto" }}>
            {specialsLoading
              ? Array.from({ length: 5 }).map((_, i) => (
                  <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 2 }}>
                    <Skeleton variant="rounded" width={40} height={40} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="60%" height={18} />
                      <Skeleton width="90%" height={14} sx={{ mt: 0.5 }} />
                    </Box>
                  </Box>
                ))
              : (specialsData || []).map((item) => (
                  <Box
                    key={item.property_id}
                    sx={{
                      display: "flex",
                      gap: 1.5,
                      mb: 1.5,
                      p: 1,
                      borderRadius: 1.5,
                      transition: "background-color 0.15s",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    <Avatar
                      src={item.primary_image_url || undefined}
                      variant="rounded"
                      sx={{ width: 40, height: 40, fontSize: 14 }}
                    >
                      {item.property_name?.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          noWrap
                          sx={{ flex: 1 }}
                        >
                          {item.property_name}
                        </Typography>
                        {item.neighborhood && (
                          <Chip
                            label={item.neighborhood}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          lineHeight: 1.4,
                          mt: 0.25,
                        }}
                      >
                        {item.specials_summary} · Ends {item.ends_on}
                      </Typography>
                    </Box>
                  </Box>
                ))}
            {!specialsLoading &&
              (!specialsData || specialsData.length === 0) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  No active specials
                </Typography>
              )}
          </Box>
        </DialogContent>
        <DialogActions>

        </DialogActions>
      </Dialog>
      {/* Commission Dialog */}
      <Dialog
        open={commissionDialogOpen}
        onClose={() => setCommissionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 7, position: "relative" }}><span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => setCommissionDialogOpen(false)}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CommissionIcon sx={{ color: theme.palette.success.dark }} />
            <Typography variant="h6" fontWeight={700}>
              Properties That Pay Commission
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ p: 2, maxHeight: 500, overflow: "auto" }}>
            {properties
              .filter(
                (p) =>
                  Number(p.learned_commission?.value) > 0 ||
                  (p.commission_with_tour && p.commission_with_tour > 0) ||
                  (p.commission_without_tour && p.commission_without_tour > 0)
              )
              .map((p) => (
                <Box
                  key={p.id}
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    mb: 1.5,
                    p: 1,
                    borderRadius: 1.5,
                    transition: "background-color 0.15s",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <Avatar
                    src={p.s3_image_url || undefined}
                    variant="rounded"
                    sx={{ width: 40, height: 40, fontSize: 14 }}
                  >
                    {p.name?.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        noWrap
                        sx={{ flex: 1 }}
                      >
                        {p.name}
                      </Typography>
                      {p.neighborhood && (
                        <Chip
                          label={p.neighborhood}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      )}
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1.4, mt: 0.25, display: "block" }}
                    >
                      {p.learned_commission ? `Invoice basis: ${p.learned_commission.basis === 'flat' ? '$' : ''}${p.learned_commission.value}${p.learned_commission.basis === 'percentage' ? '%' : ' flat fee'} · ` : ''}
                      {p.commission_with_tour
                        ? `With Tour: ${p.commission_type === "flat" ? `$${p.commission_with_tour}` : `${p.commission_with_tour}%`}`
                        : ""}
                      {p.commission_with_tour && p.commission_without_tour
                        ? " · "
                        : ""}
                      {p.commission_without_tour
                        ? `Without Tour: ${p.commission_type === "flat" ? `$${p.commission_without_tour}` : `${p.commission_without_tour}%`}`
                        : ""}
                    </Typography>
                  </Box>
                </Box>
              ))}
            {properties.filter(
              (p) =>
                Number(p.learned_commission?.value) > 0 ||
                  (p.commission_with_tour && p.commission_with_tour > 0) ||
                (p.commission_without_tour && p.commission_without_tour > 0)
            ).length === 0 && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: "center", py: 4 }}
              >
                No properties with commission
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>

        </DialogActions>
      </Dialog>

      <Drawer
        anchor="right"
        open={agentInsightsOpen}
        onClose={handleCloseAgentInsights}
        PaperProps={{ sx: { width: { xs: "100%", sm: 420 } } }}
      >
        <Box sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TrendingUpIcon color="primary" />
              {/* Grant 2026-09-24: this drawer is "Availability"; the Insights TAB keeps its name. */}
              <Typography variant="h6" fontWeight={700} data-testid="availability-drawer-title">
                Availability
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleCloseAgentInsights}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Core market signals and quick actions for your current inventory.
          </Typography>

          <Paper
            variant="outlined"
            sx={{
              mb: 2.5,
              borderRadius: 1.5,
              overflow: "hidden",
              bgcolor: "background.paper",
            }}
          >
            {(
              [
                {
                  label: "Active",
                  value: (stats?.active || properties.length).toLocaleString(),
                  icon: <ApartmentIcon fontSize="small" />,
                  color: theme.palette.primary.main,
                },
                {
                  label: "Available Units",
                  value: totalAvailableUnits.toLocaleString(),
                  icon: <AvailableIcon fontSize="small" />,
                  color: theme.palette.success.main,
                },
                {
                  label: "With Specials",
                  value: (
                    stats?.with_specials || specialsData.length
                  ).toLocaleString(),
                  icon: <SpecialIcon fontSize="small" />,
                  color: theme.palette.warning.main,
                },
                {
                  label: "Avg Start Rent",
                  value: avgStartingRent
                    ? `$${avgStartingRent.toLocaleString()}`
                    : "—",
                  icon: <TrendingUpIcon fontSize="small" />,
                  color: theme.palette.info.main,
                },
              ] as const
            ).map((row, idx, arr) => (
              <Box
                key={row.label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 1,
                  borderBottom:
                    idx < arr.length - 1
                      ? "1px solid"
                      : "none",
                  borderColor: "divider",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    minWidth: 0,
                  }}
                >
                  <Box
                    sx={{
                      color: row.color,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {row.icon}
                  </Box>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {row.label}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  sx={{ fontVariantNumeric: "tabular-nums", ml: 2 }}
                >
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Paper>

          {!isMobile ? (
            <Box sx={{ mb: 2 }}>
              <AvailabilityTimeline
                data={insightsTimelineData}
                isLoading={insightsTimelineLoading}
                onBarClick={handleInsightsTimelineBarClick}
                selectedDate={insightsSelectedDate}
                includeAvailableNow={insightsIncludeAvailableNow}
                onIncludeAvailableNowChange={setInsightsIncludeAvailableNow}
              />
            </Box>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" fontWeight={700}>
                  Upcoming Availability
                </Typography>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      size="small"
                      checked={insightsIncludeAvailableNow}
                      onChange={(e) =>
                        setInsightsIncludeAvailableNow(e.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="caption" color="text.secondary">
                      Incl. now
                    </Typography>
                  }
                />
              </Box>
              {insightsTimelineLoading ? (
                <Stack spacing={0.75}>
                  {[0, 1, 2, 3, 4].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={44}
                      sx={{ borderRadius: 1.5 }}
                    />
                  ))}
                </Stack>
              ) : (() => {
                const rows = (insightsTimelineData || [])
                  .filter((d) => d.count > 0)
                  .slice()
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 6)
                  .sort((a, b) => a.date.localeCompare(b.date));
                if (rows.length === 0) {
                  return (
                    <Box
                      sx={{
                        py: 2.5,
                        textAlign: "center",
                        border: `1px dashed ${theme.palette.divider}`,
                        borderRadius: 1.5,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No upcoming availability in the next 90 days
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <Stack spacing={0.75}>
                    {rows.map((point) => {
                      const selected = insightsSelectedDate === point.date;
                      const label = new Date(point.date + "T00:00:00").toLocaleDateString(
                        undefined,
                        { month: "short", day: "numeric" }
                      );
                      const avg = point.avg_price
                        ? `avg $${Math.round(point.avg_price / 100) / 10}k`
                        : null;
                      return (
                        <Box
                          key={point.date}
                          onClick={() => handleInsightsTimelineBarClick(point.date)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 1.25,
                            py: 1,
                            borderRadius: 1.5,
                            border: `1px solid ${selected ? theme.palette.primary.main : alpha(theme.palette.divider, 0.8)}`,
                            backgroundColor: selected
                              ? alpha(theme.palette.primary.main, 0.08)
                              : "transparent",
                            cursor: "pointer",
                            "&:hover": {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.06
                              ),
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 52 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {label}
                            </Typography>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2">
                              {point.count} unit{point.count === 1 ? "" : "s"}
                            </Typography>
                            {avg && (
                              <Typography variant="caption" color="text.secondary">
                                {avg}
                              </Typography>
                            )}
                          </Box>
                          <ChevronRightIcon
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                        </Box>
                      );
                    })}
                  </Stack>
                );
              })()}
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="subtitle2" fontWeight={700}>
              Active Specials
            </Typography>
            <Button size="small" onClick={() => setSpecialsDialogOpen(true)}>
              View all
            </Button>
          </Box>
          {specialsData.slice(0, 5).map((item) => (
            <Box
              key={item.property_id}
              sx={{
                display: "flex",
                gap: 1.25,
                p: 1,
                borderRadius: 1.5,
                mb: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              }}
            >
              <Avatar
                src={item.primary_image_url || undefined}
                variant="rounded"
                sx={{ width: 34, height: 34, fontSize: 13 }}
              >
                {item.property_name?.charAt(0)}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {item.property_name}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item.specials_summary} · Ends {item.ends_on}
                </Typography>
              </Box>
            </Box>
          ))}
          {specialsData.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No active specials in the current dataset.
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Availability Watchlist
          </Typography>
          {insightWatchlist.map((property) => (
            <Box
              key={property.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 1,
                borderRadius: 1.5,
                mb: 1,
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              }}
            >
              <Box sx={{ minWidth: 0, pr: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {property.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {property.neighborhood || "Unknown neighborhood"}
                </Typography>
              </Box>
              <Stack direction="row" spacing={0.5}>
                <Chip
                  size="small"
                  label={`${property.available_units || 0} open`}
                  color="success"
                  variant="outlined"
                />
                {property.min_price && (
                  <Chip
                    size="small"
                    label={`From $${property.min_price.toLocaleString()}`}
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          ))}
          {insightWatchlist.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No available inventory to highlight.
            </Typography>
          )}

          <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => invalidateAll()}
            >
              Refresh Data
            </Button>
          </Box>
        </Box>
      </Drawer>

      <UnitsListDrawer
        open={insightsUnitsOpen}
        onClose={() => {
          setInsightsUnitsOpen(false);
          setInsightsSelectedDate(null);
        }}
        units={insightsUnitsByDate}
        isLoading={insightsUnitsByDateLoading}
        selectedDate={insightsSelectedDate}
        onUnitClick={handleInsightsUnitClick}
      />

      <UnitDetailDrawer
        open={insightsUnitDetailOpen}
        onClose={() => {
          setInsightsUnitDetailOpen(false);
          setInsightsSelectedUnitId(null);
        }}
        unit={insightsSelectedUnit || null}
        isLoading={insightsSelectedUnitLoading}
        onPropertyClick={handleInsightsPropertyClick}
      />
    </Box>
  );
};

export default PropertiesPage;
